import { useMemo, useState } from "react";
import { Search, XCircle, AlertCircle } from "lucide-react";
import { Chip, Input } from "@/components/kit";
import ItemRow from "@/components/conference/ItemRow";

import { norm } from "@/lib/search";

function useSectors(items, isCounted) {
  return useMemo(() => {
    const acc = {};
    items.filter((i) => i.sector_id).forEach((i) => {
      const x = (acc[i.sector_id] = acc[i.sector_id] || { id: i.sector_id, name: i.sector_name, total: 0, done: 0 });
      x.total += 1;
      if (isCounted(i)) x.done += 1;
    });
    return Object.values(acc);
  }, [items, isCounted]);
}

function matches(i, filter, q, isCounted) {
  if (filter === "pending" && isCounted(i)) return false;
  if (filter === "divergent" && !i.divergent) return false;
  if (filter.startsWith("sec:") && i.sector_id !== filter.slice(4)) return false;
  return !q || norm(i.product_name).includes(q);
}

function Filters({ filter, setFilter, pending, divergent, sectors }) {
  return (
    <div className="scroll-x">
      <Chip active={filter === "all"} onClick={() => setFilter("all")} data-testid="conf-chip-all">Todas</Chip>
      <Chip active={filter === "pending"} onClick={() => setFilter("pending")} data-testid="conf-chip-pending">Pendentes ({pending})</Chip>
      {divergent > 0 && <Chip active={filter === "divergent"} onClick={() => setFilter("divergent")} data-testid="conf-chip-divergent">Divergentes ({divergent})</Chip>}
      {sectors.map((x) => <Chip key={x.id} active={filter === `sec:${x.id}`} onClick={() => setFilter(`sec:${x.id}`)} data-testid={`conf-chip-sector-${x.id}`}>{x.name}</Chip>)}
    </div>
  );
}

function SearchBox({ q, setQ }) {
  return (
    <div className="relative">
      <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 muted" />
      <Input className="pl-10" placeholder="Buscar produto" value={q} onChange={(e) => setQ(e.target.value)} data-testid="conference-search-input" />
      {q && <button className="absolute right-3 top-1/2 -translate-y-1/2 muted" onClick={() => setQ("")} data-testid="conference-search-clear"><XCircle size={17} /></button>}
    </div>
  );
}

function DivergenceAlert({ count, threshold, onClick }) {
  if (!count) return null;
  return (
    <button className="flex items-center gap-3 rounded-xl p-3 text-left" style={{ background: "var(--accent-soft)" }} onClick={onClick} data-testid="divergence-alert">
      <AlertCircle size={18} style={{ color: "var(--accent)" }} />
      <span className="label">{count} contagem(ns) fora do normal (±{threshold.toLocaleString("pt-BR")}% da média das últimas conferências). Toque para revisar.</span>
    </button>
  );
}

export default function ItemsPanel({ conf, ed, isMgmt, filter, setFilter }) {
  const [q, setQ] = useState("");
  const open = conf.status === "aberta";
  const divergent = conf.items.filter((i) => i.divergent).length;
  const sectors = useSectors(conf.items, ed.isCounted);
  const nq = norm(q.trim());
  const visible = conf.items.filter((i) => matches(i, filter, nq, ed.isCounted));
  return (
    <>
      <DivergenceAlert count={divergent} threshold={conf.divergence_threshold} onClick={() => setFilter("divergent")} />
      {sectors.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="sector-progress">
          {sectors.map((x) => <span key={x.id} className={`badge ${x.done === x.total ? "badge-success" : "badge-muted"}`} data-testid={`sector-progress-${x.id}`}>{x.name} {x.done}/{x.total}</span>)}
        </div>
      )}
      <Filters filter={filter} setFilter={setFilter} pending={ed.pending.length} divergent={divergent} sectors={sectors} />
      <div className="h3" data-testid="items-title">Itens para contagem ({visible.length})</div>
      <SearchBox q={q} setQ={setQ} />
      {conf.items.length === 0 && <p className="caption">Nenhum produto ativo. Cadastre produtos para que apareçam aqui.</p>}
      {conf.items.length > 0 && visible.length === 0 && <p className="caption">{q ? `Nenhum produto encontrado para “${q}”.` : "Nenhum item neste filtro."}</p>}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
        {visible.map((it) => (
          <ItemRow key={it.product_id} it={it} draft={ed.drafts[it.product_id] || {}} editable={open && it.editable} finalOnly={!isMgmt} hidePurchases={ed.mode === "notas"}
            confId={conf.id} onConfUpdated={(d, kind) => ed.onEntries(it.product_id, d, kind)}
            onChange={(f, v) => ed.setItem(it.product_id, f, v)} onToggleCounted={(v) => ed.setItem(it.product_id, "counted", v)} />
        ))}
      </div>
    </>
  );
}
